<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { emitter } from 'shared/helpers/mitt';
import CallManager from 'shared/helpers/call/callManager';

const activeCall = ref(null);
const callState = ref('idle'); // idle, calling, connected, ended
const isMuted = ref(false);
const callDuration = ref(0);
const durationInterval = ref(null);
const remoteStream = ref(null);
const remoteAudioRef = ref(null);
const conversationId = ref(null);
const inboxId = ref(null);

let resetStateTimeout = null;

const handleStartCall = async (data) => {
  if (resetStateTimeout) {
    clearTimeout(resetStateTimeout);
    resetStateTimeout = null;
  }

  // Clean up previous call if any
  if (activeCall.value) {
    const prevCall = activeCall.value;
    activeCall.value = null;
    prevCall.disconnect();
  }

  conversationId.value = data.conversationId;
  inboxId.value = data.inboxId;
  callDuration.value = 0;
  isMuted.value = false;
  callState.value = 'calling';

  // "Bless" the audio element inside the user interaction context to bypass browser autoplay blocks
  if (remoteAudioRef.value) {
    remoteAudioRef.value.play().catch(() => {});
  }

  // Initialize the CallAdapter using the CallManager registry
  const adapter = CallManager.initialize('native_webrtc', {
    sendSignal: (signalData) => {
      console.log('WebRTCPanel: sendSignal triggered', signalData);
      if (window.actionCable && window.actionCable.subscription && conversationId.value) {
        console.log('WebRTCPanel: Dispatching signal to ActionCable');
        window.actionCable.subscription.perform('webrtc_signal', {
          conversation_id: conversationId.value,
          signal: signalData.signal
        });
      }
    }
  });

  activeCall.value = adapter;

  adapter.addEventListener('track', (stream) => {
    remoteStream.value = stream;
    if (remoteAudioRef.value) {
      remoteAudioRef.value.srcObject = stream;
      remoteAudioRef.value.play().catch(() => {});
    }
  });

  adapter.addEventListener('disconnected', () => {
    if (activeCall.value === adapter) {
      hangup({ notifyRemote: false });
    }
  });

  // Connect WebRTC adapter (as caller)
  try {
    await adapter.connect({ isCaller: true, conversationId: conversationId.value });
  } catch (error) {
    console.error('WebRTCPanel: Failed to connect WebRTC call:', error);
    hangup({ notifyRemote: true });
  }
};

const handleWebRTCSignal = async ({ signal, conversation_id }) => {
  if (conversation_id !== conversationId.value) return;

  if (signal.type === 'hangup') {
    hangup({ notifyRemote: false });
  } else if (activeCall.value) {
    if (signal.type === 'answer') {
      callState.value = 'connected';
      callDuration.value = 0;
      clearInterval(durationInterval.value);
      durationInterval.value = setInterval(() => {
        callDuration.value += 1;
      }, 1000);
    }
    const adapter = CallManager.getAdapter();
    if (adapter) {
      await adapter.handleSignal(signal);
    }
  }
};

const hangup = ({ notifyRemote = true } = {}) => {
  if (resetStateTimeout) {
    clearTimeout(resetStateTimeout);
    resetStateTimeout = null;
  }

  const call = activeCall.value;
  if (call) {
    activeCall.value = null;
    call.disconnect();
  }

  // Send hangup event ONLY if we are manually hanging up (local hangup)
  if (notifyRemote && conversationId.value) {
    if (window.actionCable && window.actionCable.subscription) {
      window.actionCable.subscription.perform('webrtc_signal', {
        conversation_id: conversationId.value,
        signal: {
          type: 'hangup',
          duration: callDuration.value
        }
      });
    }
  }

  if (remoteAudioRef.value) {
    remoteAudioRef.value.srcObject = null;
  }
  remoteStream.value = null;
  clearInterval(durationInterval.value);
  callState.value = 'ended';

  resetStateTimeout = setTimeout(() => {
    callState.value = 'idle';
    conversationId.value = null;
    inboxId.value = null;
    resetStateTimeout = null;
  }, 1500);
};

const toggleMute = () => {
  isMuted.value = !isMuted.value;
  if (activeCall.value) {
    activeCall.value.setMuted(isMuted.value);
  }
};

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60).toString().padStart(2, '0');
  const secs = (seconds % 60).toString().padStart(2, '0');
  return `${mins}:${secs}`;
};

onMounted(() => {
  emitter.on('webrtc:start-call', handleStartCall);
  emitter.on('webrtc:signal', handleWebRTCSignal);
});

onUnmounted(() => {
  emitter.off('webrtc:start-call', handleStartCall);
  emitter.off('webrtc:signal', handleWebRTCSignal);
  clearInterval(durationInterval.value);
  if (resetStateTimeout) {
    clearTimeout(resetStateTimeout);
  }
});
</script>

<template>
  <div>
    <audio ref="remoteAudioRef" autoplay class="hidden" />

    <div v-if="callState !== 'idle'" class="fixed bottom-4 right-4 z-[99999] bg-white dark:bg-slate-900 shadow-2xl rounded-2xl border border-slate-200 dark:border-slate-800 p-4 flex items-center justify-between text-slate-800 dark:text-slate-100 w-80">

    <div class="flex items-center gap-3">
      <div class="w-10 h-10 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-500 animate-pulse">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
        </svg>
      </div>
      <div class="flex flex-col">
        <span class="text-xs text-slate-500 font-medium">WEBRTC VOICE CALL</span>
        <span v-if="callState === 'calling'" class="text-sm font-bold">Calling Visitor...</span>
        <span v-else-if="callState === 'connected'" class="text-sm font-bold">Connected • {{ formatTime(callDuration) }}</span>
        <span v-else-if="callState === 'ended'" class="text-sm font-bold text-red-500">Call Ended</span>
      </div>
    </div>

    <div class="flex gap-2">
      <!-- Mute Button -->
      <button v-if="callState === 'connected'" class="bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-full p-2 flex items-center justify-center transition-colors" :class="{ 'bg-red-500/10 !text-red-500': isMuted }" @click="toggleMute">
        <svg v-if="!isMuted" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3z"/>
          <path d="M19 10v1a7 7 0 0 1-14 0v-1"/>
          <line x1="12" y1="19" x2="12" y2="22"/>
        </svg>
        <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 9a3 3 0 0 0 3 3m3-3V5a3 3 0 0 0-3-3 3 3 0 0 0-3 3v4"/>
          <path d="M19 10v1a7.88 7.88 0 0 1-.4 2.5"/>
          <path d="M6 10a7.92 7.92 0 0 0 4.8 6.9"/>
          <line x1="12" y1="19" x2="12" y2="22"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
      </button>
      <!-- Hangup Button -->
      <button class="bg-red-500 hover:bg-red-600 text-white rounded-full p-2.5 flex items-center justify-center transition-colors" @click="hangup">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a1 1 0 0 1 1-.27 10.3 10.3 0 0 0 3.28.51 1 1 0 0 1 1 1v3.46a1 1 0 0 1-1 1.05A19 19 0 0 1 3 4.05 1 1 0 0 1 4 3h3.46a1 1 0 0 1 1 1 10.3 10.3 0 0 0 .51 3.28 1 1 0 0 1-.27 1L8.43 8.55a16 16 0 0 0 2.25 4.76z"/>
          <line x1="23" y1="1" x2="1" y2="23"/>
        </svg>
      </button>
    </div>
  </div>
</div>
</template>
